import { useTranslation } from '@kostbar/i18n'
import { Info } from 'lucide-react'
import type {
  OpsBackups,
  PaymentCardDefaults,
  PrinterDefaults,
} from './edge-settings-utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'

type SettingsTabProps = {
  printer: PrinterDefaults
  payment: PaymentCardDefaults
  ops: OpsBackups
  onSetPrinter: React.Dispatch<React.SetStateAction<PrinterDefaults>>
  onSetPayment: React.Dispatch<React.SetStateAction<PaymentCardDefaults>>
  onSetOps: React.Dispatch<React.SetStateAction<OpsBackups>>
}

function InfoPopover({ text }: { text: string }) {
  const { t: tRestaurants } = useTranslation('restaurants')

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-foreground"
          aria-label={tRestaurants('edgeSettings.infoAria')}
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 text-xs leading-relaxed" align="start">
        {text}
      </PopoverContent>
    </Popover>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="text-sm font-medium text-muted-foreground">{children}</h4>
  )
}

export function SettingsTab({
  printer,
  payment,
  ops,
  onSetPrinter,
  onSetPayment,
  onSetOps,
}: SettingsTabProps) {
  const { t: tRestaurants } = useTranslation('restaurants')

  return (
    <div className="space-y-6">
      {/* ── Printer Defaults ── */}
      <Card>
        <CardHeader>
          <CardTitle>
            {tRestaurants('edgeSettings.printerDefaults.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="w-40 space-y-2">
            <div className="flex items-center gap-2">
              <Label>
                {tRestaurants('edgeSettings.printerDefaults.codePage')}
              </Label>
              <InfoPopover
                text={tRestaurants('edgeSettings.printerDefaults.codePageInfo')}
              />
            </div>
            <Select
              value={printer.codePage}
              onValueChange={(value) =>
                onSetPrinter((prev) => ({ ...prev, codePage: value }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={tRestaurants(
                    'edgeSettings.printerDefaults.codePagePlaceholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cp1252">
                  {tRestaurants(
                    'edgeSettings.printerDefaults.codePages.cp1252',
                  )}
                </SelectItem>
                <SelectItem value="cp850">
                  {tRestaurants('edgeSettings.printerDefaults.codePages.cp850')}
                </SelectItem>
                <SelectItem value="cp437">
                  {tRestaurants('edgeSettings.printerDefaults.codePages.cp437')}
                </SelectItem>
                <SelectItem value="cp858">
                  {tRestaurants('edgeSettings.printerDefaults.codePages.cp858')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Separator />

          <div className="space-y-4">
            <SectionHeading>
              {tRestaurants('edgeSettings.printerDefaults.qrSection')}
            </SectionHeading>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
              <div className="flex items-center gap-3 pb-1">
                <Checkbox
                  checked={printer.qrEnabled}
                  onCheckedChange={(value) =>
                    onSetPrinter((prev) => ({
                      ...prev,
                      qrEnabled: Boolean(value),
                    }))
                  }
                />
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.printerDefaults.qrEnabled')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants(
                      'edgeSettings.printerDefaults.qrEnabledInfo',
                    )}
                  />
                </div>
              </div>
              <div className="w-20 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.printerDefaults.qrSize')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants(
                      'edgeSettings.printerDefaults.qrSizeInfo',
                    )}
                  />
                </div>
                <Input
                  type="number"
                  min={1}
                  max={16}
                  value={printer.qrSize}
                  onChange={(e) =>
                    onSetPrinter((prev) => ({
                      ...prev,
                      qrSize: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="w-24 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.printerDefaults.qrEcLevel')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants(
                      'edgeSettings.printerDefaults.qrEcLevelInfo',
                    )}
                  />
                </div>
                <Select
                  value={printer.qrEcLevel}
                  onValueChange={(value) =>
                    onSetPrinter((prev) => ({ ...prev, qrEcLevel: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={tRestaurants(
                        'edgeSettings.printerDefaults.qrEcLevelPlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="L">
                      {tRestaurants(
                        'edgeSettings.printerDefaults.qrEcLevels.L',
                      )}
                    </SelectItem>
                    <SelectItem value="M">
                      {tRestaurants(
                        'edgeSettings.printerDefaults.qrEcLevels.M',
                      )}
                    </SelectItem>
                    <SelectItem value="Q">
                      {tRestaurants(
                        'edgeSettings.printerDefaults.qrEcLevels.Q',
                      )}
                    </SelectItem>
                    <SelectItem value="H">
                      {tRestaurants(
                        'edgeSettings.printerDefaults.qrEcLevels.H',
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Payment (Card) ── */}
      <Card>
        <CardHeader>
          <CardTitle>{tRestaurants('edgeSettings.payment.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* ZVT Protocol */}
          <div className="space-y-4">
            <SectionHeading>
              {tRestaurants('edgeSettings.payment.sectionProtocol')}
            </SectionHeading>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
              <div className="w-28 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>{tRestaurants('edgeSettings.payment.t3Label')}</Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.t3Info')}
                  />
                </div>
                <Input
                  type="number"
                  min={100}
                  value={payment.zvtDefaults.timeout3Ms}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        timeout3Ms: Number(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div className="w-28 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>{tRestaurants('edgeSettings.payment.t4Label')}</Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.t4Info')}
                  />
                </div>
                <Input
                  type="number"
                  min={1000}
                  value={payment.zvtDefaults.timeout4Ms}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        timeout4Ms: Number(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div className="w-24 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.configByte')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.configByteInfo')}
                  />
                </div>
                <Input
                  value={payment.zvtDefaults.configByte}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        configByte: e.target.value.toUpperCase(),
                      },
                    }))
                  }
                />
              </div>
              <div className="w-28 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>{tRestaurants('edgeSettings.payment.delay')}</Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.delayInfo')}
                  />
                </div>
                <Input
                  type="number"
                  min={0}
                  value={payment.zvtDefaults.delayBetweenCommandsMs}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        delayBetweenCommandsMs: Number(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div className="w-44 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.cardReceiptPrint')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants(
                      'edgeSettings.payment.cardReceiptPrintInfo',
                    )}
                  />
                </div>
                <Select
                  value={payment.zvtDefaults.cardReceiptPrint}
                  onValueChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        cardReceiptPrint: value,
                      },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={tRestaurants(
                        'edgeSettings.payment.cardReceiptPrintPlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ecr">
                      {tRestaurants(
                        'edgeSettings.payment.cardReceiptPrintOptions.ecr',
                      )}
                    </SelectItem>
                    <SelectItem value="terminal">
                      {tRestaurants(
                        'edgeSettings.payment.cardReceiptPrintOptions.terminal',
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24 space-y-2">
                <Label>{tRestaurants('edgeSettings.payment.language')}</Label>
                <Select
                  value={payment.zvtDefaults.language}
                  onValueChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: { ...prev.zvtDefaults, language: value },
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={tRestaurants(
                        'edgeSettings.payment.languagePlaceholder',
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DE">
                      {tRestaurants('edgeSettings.payment.languages.DE')}
                    </SelectItem>
                    <SelectItem value="EN">
                      {tRestaurants('edgeSettings.payment.languages.EN')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Behavior toggles */}
          <div className="space-y-4">
            <SectionHeading>
              {tRestaurants('edgeSettings.payment.sectionBehavior')}
            </SectionHeading>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={payment.zvtDefaults.forceRegistration}
                  onCheckedChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      zvtDefaults: {
                        ...prev.zvtDefaults,
                        forceRegistration: Boolean(value),
                      },
                    }))
                  }
                />
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.forceRegistration')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants(
                      'edgeSettings.payment.forceRegistrationInfo',
                    )}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={payment.autoLogonOnStartup}
                  onCheckedChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      autoLogonOnStartup: Boolean(value),
                    }))
                  }
                />
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.autoLogon')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.autoLogonInfo')}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={payment.healthGateRouting}
                  onCheckedChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      healthGateRouting: Boolean(value),
                    }))
                  }
                />
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.healthGate')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.healthGateInfo')}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={payment.useStub}
                  onCheckedChange={(value) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      useStub: Boolean(value),
                    }))
                  }
                />
                <div className="flex items-center gap-2">
                  <Label>{tRestaurants('edgeSettings.payment.useStub')}</Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.useStubInfo')}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Timeouts & Polling */}
          <div className="space-y-4">
            <SectionHeading>
              {tRestaurants('edgeSettings.payment.sectionTimings')}
            </SectionHeading>
            <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
              <div className="w-28 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.healthPoll')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.healthPollInfo')}
                  />
                </div>
                <Input
                  type="number"
                  min={1}
                  value={payment.healthPollSeconds}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      healthPollSeconds: Number(e.target.value),
                    }))
                  }
                />
              </div>
              <div className="w-32 space-y-2">
                <div className="flex items-center gap-2">
                  <Label>
                    {tRestaurants('edgeSettings.payment.hobexTimeout')}
                  </Label>
                  <InfoPopover
                    text={tRestaurants('edgeSettings.payment.hobexTimeoutInfo')}
                  />
                </div>
                <Input
                  type="number"
                  min={100}
                  value={payment.hobexEcr.timeoutMs}
                  onChange={(e) =>
                    onSetPayment((prev) => ({
                      ...prev,
                      hobexEcr: { timeoutMs: Number(e.target.value) },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Ops / Backups ── */}
      <Card>
        <CardHeader>
          <CardTitle>{tRestaurants('edgeSettings.ops.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={ops.enabled}
              onCheckedChange={(value) =>
                onSetOps((prev) => ({ ...prev, enabled: Boolean(value) }))
              }
            />
            <Label>{tRestaurants('edgeSettings.ops.enabled')}</Label>
          </div>
          <Separator />
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <div className="w-48 space-y-2">
              <Label>{tRestaurants('edgeSettings.ops.dir')}</Label>
              <Input
                value={ops.dir}
                onChange={(e) =>
                  onSetOps((prev) => ({ ...prev, dir: e.target.value }))
                }
              />
            </div>
            <div className="w-48 space-y-2">
              <Label>{tRestaurants('edgeSettings.ops.dbPath')}</Label>
              <Input
                value={ops.dbPath}
                onChange={(e) =>
                  onSetOps((prev) => ({ ...prev, dbPath: e.target.value }))
                }
              />
            </div>
            <div className="w-40 space-y-2">
              <Label>{tRestaurants('edgeSettings.ops.cron')}</Label>
              <Input
                value={ops.cron}
                onChange={(e) =>
                  onSetOps((prev) => ({ ...prev, cron: e.target.value }))
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
